import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { buildCartLineKey } from "../utils/modifierUtils";

// Create the context
const CartContext = createContext();
const ORDER_NOTE_STORAGE_KEY = "cartOrderNote";
const CART_QR_CONTEXT_STORAGE_KEY = "cartQrContext:v1";

// Load cart from localStorage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error("Error loading cart from storage:", error);
    return [];
  }
};

const loadOrderNoteFromStorage = () => {
  try {
    return localStorage.getItem(ORDER_NOTE_STORAGE_KEY) || "";
  } catch (error) {
    console.error("Error loading order note from storage:", error);
    return "";
  }
};

const loadCartQrContextFromStorage = () => {
  try {
    const savedContext = localStorage.getItem(CART_QR_CONTEXT_STORAGE_KEY);
    if (!savedContext) {
      return null;
    }

    const parsed = JSON.parse(savedContext);
    if (
      Number.isInteger(parsed?.venueId) &&
      parsed.venueId > 0 &&
      Number.isInteger(parsed?.tableId) &&
      parsed.tableId > 0
    ) {
      return {
        venueId: parsed.venueId,
        tableId: parsed.tableId,
      };
    }

    return null;
  } catch (error) {
    console.error("Error loading cart QR context from storage:", error);
    return null;
  }
};

const normalizeQrContext = (context) => {
  if (
    Number.isInteger(context?.venueId) &&
    context.venueId > 0 &&
    Number.isInteger(context?.tableId) &&
    context.tableId > 0
  ) {
    return {
      venueId: context.venueId,
      tableId: context.tableId,
    };
  }

  return null;
};

const isSameQrContext = (left, right) =>
  left?.venueId === right?.venueId && left?.tableId === right?.tableId;

// Provider component
export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(loadCartFromStorage);
  const [orderNote, setOrderNoteState] = useState(loadOrderNoteFromStorage);
  const [cartQrContext, setCartQrContext] = useState(loadCartQrContextFromStorage);
  const cartRef = useRef(cart);
  const orderNoteRef = useRef(orderNote);
  const cartQrContextRef = useRef(cartQrContext);

  cartRef.current = cart;
  orderNoteRef.current = orderNote;
  cartQrContextRef.current = cartQrContext;

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cart));
    } catch (error) {
      console.error("Error saving cart to storage:", error);
    }
  }, [cart]);

  useEffect(() => {
    try {
      localStorage.setItem(ORDER_NOTE_STORAGE_KEY, orderNote);
    } catch (error) {
      console.error("Error saving order note to storage:", error);
    }
  }, [orderNote]);

  useEffect(() => {
    try {
      if (cartQrContext) {
        localStorage.setItem(CART_QR_CONTEXT_STORAGE_KEY, JSON.stringify(cartQrContext));
      } else {
        localStorage.removeItem(CART_QR_CONTEXT_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Error saving cart QR context:", error);
    }
  }, [cartQrContext]);

  const getCartItemKey = useCallback((item) => item?.lineKey || item?.id, []);

  const addItem = (item, quantity = 1) => {
    const lineKey = item.lineKey || buildCartLineKey(item);
    const nextItem = {
      ...item,
      ...(lineKey ? { lineKey } : {}),
      quantity: item.quantity || quantity,
      notes: item.notes || "",
    };

    setCart((prev) => {
      const existing = prev.find((entry) => getCartItemKey(entry) === (lineKey || item.id));
      if (existing) {
        return prev.map((x) =>
          getCartItemKey(x) === (lineKey || item.id) ? { ...x, quantity: x.quantity + quantity } : x
        );
      }

      return [...prev, nextItem];
    });
  };

  const removeItem = (item) => {
    const itemKey = getCartItemKey(item);
    setCart((prev) => {
      const existing = prev.find((entry) => getCartItemKey(entry) === itemKey);
      if (existing && existing.quantity > 1) {
        return prev.map((x) =>
          getCartItemKey(x) === itemKey ? { ...x, quantity: x.quantity - 1 } : x
        );
      }

      return prev.filter((entry) => getCartItemKey(entry) !== itemKey);
    });
  };

  const removeItemsByIds = useCallback((itemIds) => {
    const normalizedIds = new Set((itemIds || []).map((value) => Number(value)).filter((value) => Number.isFinite(value)));
    if (normalizedIds.size === 0) {
      return;
    }

    setCart((prev) => prev.filter((item) => !normalizedIds.has(Number(item.id))));
  }, []);

  const clearCart = () => {
    setCart([]);
    setOrderNoteState("");
  };

  const syncCartContext = useCallback((nextContext) => {
    const normalizedContext = normalizeQrContext(nextContext);
    if (!normalizedContext) {
      return { didClear: false, adoptedContext: false };
    }

    if (isSameQrContext(cartQrContextRef.current, normalizedContext)) {
      return { didClear: false, adoptedContext: false };
    }

    const hasCartData = cartRef.current.length > 0 || orderNoteRef.current.trim().length > 0;
    setCartQrContext(normalizedContext);

    if (!hasCartData) {
      return { didClear: false, adoptedContext: true };
    }

    setCart([]);
    setOrderNoteState("");
    return { didClear: true, adoptedContext: true };
  }, []);

  const setOrderNote = (note) => {
    setOrderNoteState(note);
  };

  const setItemNote = (itemKey, note) => {
    setCart((prev) =>
      prev.map((item) =>
        getCartItemKey(item) === itemKey ? { ...item, notes: note } : item
      )
    );
  };

  // Calculate total count of items for badge
  const itemCount = cart.reduce((total, item) => total + item.quantity, 0);

  const value = {
    cart, 
    addItem, 
    removeItem, 
    removeItemsByIds,
    clearCart, 
    itemCount,
    orderNote,
    setOrderNote,
    setItemNote,
    syncCartContext
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
